/**
 * Terminal auth-family latch — after family-revoked, all automatic refresh attempts
 * stop until the user signs in again. Boot-time terminal rejection (any verdict)
 * routes to sign-in before usable authed state is reached.
 */

import { classifyTerminalAuthVerdict, type AuthTerminalVerdict } from './authDeclawDeadFamily';
import { isBootHoldSignInActive } from './authBootHoldSignIn';

let _familyRevokedLatched = false;
let _bootTerminalVerdict: AuthTerminalVerdict | null = null;
let _usableAuthedReached = false;

export function markUsableAuthedStateReached(): void {
  _usableAuthedReached = true;
}

export function hasReachedUsableAuthedState(): boolean {
  return _usableAuthedReached;
}

export function latchTerminalAuthFamily(reason: AuthTerminalVerdict): void {
  const atBoot = !_usableAuthedReached;
  if (atBoot) {
    _bootTerminalVerdict = reason;
    if (shouldPurgeBootTerminalStoredSession(reason)) {
      purgeBootTerminalStoredSession();
    }
  }
  if (reason === 'family_revoked') {
    _familyRevokedLatched = true;
  }
}

export function isTerminalAuthFamilyLatched(): boolean {
  return _familyRevokedLatched;
}

export function getTerminalAuthLatchReason(): AuthTerminalVerdict | null {
  return _bootTerminalVerdict;
}

/** Boot-time terminal rejection — route to sign-in, not declaw playback shell. */
export function isBootTimeTerminalRejection(): boolean {
  return !!_bootTerminalVerdict && !_usableAuthedReached;
}

/** Storm damper — boot hold + family-revoked stop automatic refresh loops (#1499). */
export function shouldSuppressAutomaticRefresh(): boolean {
  return _familyRevokedLatched || isBootHoldSignInActive();
}

export function clearTerminalAuthFamilyLatch(): void {
  _familyRevokedLatched = false;
  _bootTerminalVerdict = null;
}

export function maybeLatchFromRefreshRejection(
  status: unknown,
  data?: unknown,
  message?: string,
): AuthTerminalVerdict | null {
  const verdict = classifyTerminalAuthVerdict(status, data, message);
  if (!verdict) return null;
  latchTerminalAuthFamily(verdict);
  return verdict;
}

export function resetAuthTerminalLatchForTests(): void {
  _familyRevokedLatched = false;
  _bootTerminalVerdict = null;
  _usableAuthedReached = false;
}

export function forceBootTerminalSignInLatch(reason: AuthTerminalVerdict): void {
  _bootTerminalVerdict = reason;
  _usableAuthedReached = false;
}

export function purgeBootTerminalStoredSession(): void {
  try { if (typeof localStorage !== 'undefined') localStorage.removeItem('maestro_auth'); } catch { /* ignore */ }
  try { if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('maestro_refresh_failed'); } catch { /* ignore */ }
}

function shouldPurgeBootTerminalStoredSession(reason: AuthTerminalVerdict): boolean {
  return reason === 'family_revoked' || reason === 'refresh_token_already_used';
}
